<template>
  <AppClientSchoolLayout title="成績">
    <div
      v-if="selectedExamination && selectedExamination.id && user && user.id"
      class="mx-auto flex w-full max-w-7xl flex-col items-stretch justify-center md:flex-row md:items-start">
      <aside class="mx-3 mt-3 w-full md:w-[320px] md:flex-none sm:mx-5">
        <section class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow">
          <div class="border-b border-gray-100 px-4 py-4">
            <p class="text-xs font-bold uppercase tracking-[0.2em] text-gray-500">
              成績対象
            </p>
            <h2 class="mt-2 text-lg font-black text-gray-900">
              学生情報
            </h2>
          </div>

          <dl class="divide-y divide-gray-100">
            <div class="px-4 py-4">
              <dt class="text-sm font-semibold text-gray-500">
                模試名
              </dt>
              <dd class="mt-2 text-sm font-medium leading-6 text-gray-900">
                {{ selectedExamination.title }}
              </dd>
            </div>
            <div class="px-4 py-4">
              <dt class="text-sm font-semibold text-gray-500">
                ユーザID
              </dt>
              <dd class="mt-2 text-sm font-medium leading-6 text-gray-900">
                {{ user.id }}
              </dd>
            </div>
            <div class="px-4 py-4">
              <dt class="text-sm font-semibold text-gray-500">
                学籍番号
              </dt>
              <dd class="mt-2 text-sm font-medium leading-6 text-gray-900">
                {{ user.student_id }}
              </dd>
            </div>
            <div class="px-4 py-4">
              <dt class="text-sm font-semibold text-gray-500">
                学年
              </dt>
              <dd class="mt-2 text-sm font-medium leading-6 text-gray-900">
                {{ convertToGrade(user.service_years) }}
              </dd>
            </div>
            <div class="px-4 py-4">
              <dt class="text-sm font-semibold text-gray-500">
                名前
              </dt>
              <dd class="mt-2 text-sm font-medium leading-6 text-gray-900">
                {{ user.name }}
              </dd>
            </div>
            <div class="px-4 py-4">
              <inertia-link
                class="inline-flex min-h-[40px] w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                :href="route('school.result', {
                  examinationId: selectedExamination.id,
                  showTabDisplay: 0,
                })">
                <svg
                  class="mr-2 h-4 w-4 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M15 19l-7-7 7-7" />
                </svg>
                一覧に戻る
              </inertia-link>
            </div>
          </dl>
        </section>
      </aside>

      <section class="mx-3 mb-5 mt-3 flex-1 overflow-visible rounded-xl bg-white text-gray-700 shadow sm:mx-5 sm:mt-5 md:max-w-4xl">
        <div class="border-b border-gray-100 px-4 py-4 sm:px-6">
          <h3 class="text-lg font-bold leading-6 text-gray-900">
            成績詳細
          </h3>
        </div>

        <div class="space-y-4 bg-gray-50 p-3 sm:space-y-5 sm:p-5">
          <section class="rounded-xl border border-gray-200 bg-white px-4 py-4">
            <p class="text-sm font-semibold text-gray-700">
              模試名
            </p>
            <p class="mt-2 text-sm font-medium leading-6 text-gray-900 sm:text-base">
              {{ selectedExamination.title }}
            </p>
          </section>

          <section
            class="overflow-hidden rounded-xl border border-gray-200 bg-white"
            aria-label="総合結果">
            <div class="grid grid-cols-1 divide-y divide-gray-200 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
              <div>
                <div class="px-4 py-4 text-center">
                  <p class="text-xs font-bold text-gray-600">
                    総合点
                  </p>
                  <p class="mt-2 flex items-baseline justify-center gap-1 text-gray-900">
                    <strong class="text-3xl leading-none tracking-normal">
                      {{ getTotalScore() }}
                    </strong>
                    <span class="text-sm font-bold text-gray-600">
                      / 200
                    </span>
                  </p>
                </div>

                <div class="grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200">
                  <div
                    v-for="(summary, index) in passScoreSummaries"
                    :key="summary.label"
                    class="px-2 py-3 sm:px-4">
                    <p class="text-xs font-bold text-gray-600">
                      {{ summary.label }}
                    </p>
                    <div
                      class="mt-2 flex min-h-[32px] flex-nowrap items-center justify-center gap-[3px] sm:gap-2"
                      :class="index === 0 ? 'sm:translate-x-2' : ''">
                      <p
                        v-if="summary.completed"
                        class="flex items-baseline gap-1 whitespace-nowrap text-gray-900">
                        <strong class="text-2xl leading-none tracking-normal">
                          {{ summary.score }}
                        </strong>
                        <span class="text-xs font-bold text-gray-600">
                          /{{ summary.max }}
                        </span>
                      </p>
                      <p
                        v-else
                        class="text-sm font-bold text-gray-500">
                        未実施
                      </p>
                      <span :class="getPassFailChipClass(summary.result, 'outlineCompact')">
                        {{ summary.result }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex min-h-[104px] flex-col items-center justify-center px-4 py-3 text-center sm:min-h-[132px] sm:py-5">
                <p class="text-xs font-bold text-gray-600">
                  合否
                </p>
                <span
                  class="mt-2"
                  :class="getPassFailChipClass(overallPassFailResult, 'solid')">
                  {{ overallPassFailResult }}
                </span>
              </div>
            </div>

            <div class="space-y-2 border-t border-gray-200 px-4 py-3">
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-sm font-bold text-gray-800">
                  全国順位
                </span>
                <template v-if="getRanking()">
                  <span class="text-base font-black text-gray-900">
                    {{ getRanking() }}位
                  </span>
                  <span class="text-xs font-semibold text-gray-500">
                    / {{ usersCount }}人中
                  </span>
                </template>
                <template v-else-if="selectedExamination.result_announcement_date">
                  <span class="inline-flex h-7 items-center rounded-full bg-gray-200 px-3 text-sm font-bold text-gray-700">
                    未集計
                  </span>
                  <span class="text-xs font-semibold text-gray-500">
                    集計後に表示されます
                  </span>
                </template>
                <template v-else>
                  <span class="inline-flex h-7 items-center rounded-full bg-gray-100 px-3 text-sm font-bold text-gray-500">
                    集計前
                  </span>
                </template>
              </div>

              <div
                v-if="getSchoolRanking()"
                class="flex flex-wrap items-center gap-2">
                <span class="text-sm font-bold text-gray-800">
                  校内順位
                </span>
                <span class="text-base font-black text-gray-900">
                  {{ getSchoolRanking() }}位
                </span>
                <span class="text-xs font-semibold text-gray-500">
                  / {{ getSchoolUserNum() }}人中
                </span>
              </div>
            </div>
          </section>

          <div class="space-y-2 text-xs font-semibold text-red-500">
            <p v-if="isBeforeDate(selectedExamination.result_announcement_date)">
              ※順位と平均点は後日集計予定となります。
            </p>
            <p v-if="isOveredDate(selectedExamination.result_announcement_date) && fiscalYearStart(selectedExamination.reception_start)">
              ※点数が()付きで表記されている科目につきましては合計点数の計算対象外となります。
            </p>
            <p v-if="isBeforeDate(selectedExamination.result_announcement_date) && !fiscalYearStart(selectedExamination.reception_start)">
              成績集計開始日時：{{ formatDate(selectedExamination.result_announcement_date) }}
            </p>
          </div>

          <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <section
              class="rounded-xl border border-gray-200 bg-white p-4"
              aria-labelledby="detail-score-title">
              <h4
                id="detail-score-title"
                class="text-sm font-black text-gray-900">
                詳細スコア
              </h4>

              <div class="mt-3 overflow-hidden rounded-lg border border-gray-100">
                <div
                  v-for="row in resultScoreRows"
                  :key="row.label"
                  class="flex min-h-[44px] items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 last:border-b-0"
                  :class="row.isTotal ? 'bg-blue-50 font-bold' : 'bg-white'">
                  <span class="whitespace-nowrap text-sm font-semibold text-gray-800">
                    {{ row.label }}
                  </span>
                  <span class="flex shrink-0 items-center gap-2">
                    <span
                      v-if="row.completed"
                      class="whitespace-nowrap text-sm font-bold text-gray-700">
                      <strong
                        class="text-gray-900"
                        :class="row.isTotal ? 'text-lg sm:text-xl' : 'text-base'">
                        <span v-if="row.isExcluded">(</span>{{ row.score }}<span v-if="row.isExcluded">)</span>
                      </strong>
                      /{{ row.max }}
                    </span>
                    <span
                      v-else
                      class="text-sm font-bold text-gray-500">
                      未実施
                    </span>
                    <span
                      v-if="row.completed"
                      :class="getGradeBadgeClass(row.rank)">
                      {{ row.rank }}
                    </span>
                    <span
                      v-else
                      class="inline-flex h-7 w-8 items-center justify-center rounded-md bg-gray-100 text-sm font-bold text-gray-400">
                      -
                    </span>
                  </span>
                </div>
              </div>
            </section>

            <section
              class="rounded-xl border border-gray-200 bg-white p-4"
              aria-labelledby="score-chart-title">
              <div class="flex flex-wrap items-start justify-between gap-2">
                <h4
                  id="score-chart-title"
                  class="text-sm font-black text-gray-900">
                  科目別スコア
                </h4>
                <div class="flex flex-col gap-1 text-[11px] font-bold text-gray-500">
                  <span class="inline-flex items-center gap-1">
                    <i class="h-2 w-3 rounded-sm bg-green-500"></i>あなたの正答率（％）
                  </span>
                  <span class="inline-flex items-center gap-1">
                    <i class="h-2 w-3 rounded-sm bg-gray-300"></i>全国平均（％）
                  </span>
                </div>
              </div>
              <div class="mt-2 h-52 overflow-hidden sm:h-56">
                <BarChart
                  :chart-data="getBarData()"
                  css-classes="h-full w-full"
                  :options="scoreBarChartOptions" />
              </div>
            </section>
          </div>

          <section
            v-if="shouldShowSubjectAnalytics"
            class="rounded-xl border border-gray-200 bg-white p-4"
            aria-labelledby="subject-analysis-title">
            <h4
              id="subject-analysis-title"
              class="text-sm font-black text-gray-900">
              分野別分析
            </h4>

            <div class="mt-0 flex flex-col gap-3 md:mt-4 md:flex-row md:items-start md:gap-4 md:pr-4 lg:pr-6">
              <div class="min-w-0 md:w-[69%] md:flex-none">
                <div
                  class="h-60 cursor-pointer sm:h-72 md:h-[360px]"
                  @click="handleSubjectChartClick">
                  <RadarChart
                    ref="subjectChartRef"
                    css-classes="h-full w-full"
                    :chart-data="subjectBreakdownChartData"
                    :options="subjectBreakdownChartOptions" />
                </div>
                <div class="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] font-bold text-gray-500 md:mt-5">
                  <span class="inline-flex items-center gap-1"><i class="h-2 w-4 rounded-sm border-2 border-green-600"></i>あなたの正答率（％）</span>
                  <span
                    v-if="isNationalAnalyticsReady"
                    class="inline-flex items-center gap-1"><i class="h-2 w-4 rounded-sm border-2 border-gray-400"></i>全国平均（％）</span>
                </div>
              </div>

              <div class="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 md:w-[31%] md:flex-none md:grid-cols-1">
                <button
                  v-for="subject in currentSubjectOptions"
                  :key="subject.id"
                  type="button"
                  class="flex min-h-[64px] items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition"
                  :class="selectedSubjectId === subject.id
                    ? 'border-blue-500 bg-blue-50 text-blue-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:text-blue-700'"
                  @click="openSubjectModal(subject.id)">
                  <span class="min-w-0 flex-1">
                    <strong class="block truncate text-sm font-bold leading-snug text-gray-900">
                      {{ subject.label }}
                    </strong>
                    <small class="mt-1 block whitespace-nowrap text-xs font-semibold text-gray-500">
                      <b class="text-green-700">あなた {{ subject.personalRate }}%</b>
                      <template v-if="isNationalAnalyticsReady">
                        <span class="mx-1">/</span>
                        全国 {{ subject.nationalRate }}%
                      </template>
                    </small>
                  </span>
                  <span class="flex h-8 w-5 shrink-0 items-center justify-center text-gray-400">
                    <svg
                      class="h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </section>

          <section
            v-else-if="shouldShowSubjectAnalyticsMissingState"
            class="rounded-xl border border-gray-200 bg-white p-4"
            aria-labelledby="subject-analysis-title">
            <h4
              id="subject-analysis-title"
              class="text-sm font-black text-gray-900">
              分野別分析
            </h4>
            <div class="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              回答明細がないため、分野別分析を表示できません。
            </div>
          </section>

          <section
            class="rounded-xl border border-gray-200 bg-white p-4"
            aria-labelledby="commentary-actions-title">
            <h4
              id="commentary-actions-title"
              class="text-sm font-black text-gray-900">
              解説・解答
            </h4>

            <div class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <a
                class="flex min-h-[46px] items-center rounded-lg bg-yellow-500 px-4 text-sm font-bold text-white shadow-sm transition lg:hover:bg-yellow-400 active:bg-yellow-400"
                :href="route('school.commentary.answer-list', { examination: selectedExamination.id, userId: user.id })"
                target="_blank">
                <span class="flex-1 text-center">解答一覧</span>
                <span class="flex h-8 w-5 shrink-0 items-center justify-center">
                  <svg
                    class="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </a>
            </div>
          </section>
        </div>
      </section>
    </div>

    <div
      v-else
      class="mx-auto flex w-full max-w-7xl justify-center px-4 py-8">
      <section class="w-full max-w-xl rounded-xl border border-gray-200 bg-white p-6 text-center text-sm font-medium text-gray-500 shadow">
        表示できる成績がありません。
      </section>
    </div>

    <DialogModal
      :show="isSubjectModalOpen"
      max-width="2xl"
      show-scroll-hint
      @close="closeSubjectModal">
      <template #title>
        <div class="flex items-center justify-between gap-3">
          <div>
            <div class="text-sm font-semibold text-gray-900 sm:text-lg">
              {{ selectedSubjectLabel }} の分野別成績
            </div>
            <div class="mt-1 text-[11px] leading-4 text-gray-500 sm:text-sm">
              {{ isNationalAnalyticsReady ? 'あなたの正答率と全国正答率を分野ごとに比較できます。' : 'あなたの正答率を分野ごとに確認できます。' }}
            </div>
          </div>
        </div>
      </template>

      <template #content>
        <div
          v-if="selectedSubjectId"
          class="space-y-5 sm:space-y-6">
          <!-- 4項目は2列、3項目は横並びにして分野別サマリーの高さを揃える -->
          <div
            class="grid gap-2 sm:gap-4"
            :class="isNationalAnalyticsReady ? 'grid-cols-2' : 'grid-cols-3'">
            <div class="flex min-h-[94px] flex-col items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-3 text-center shadow-sm sm:px-4 sm:py-4">
              <div class="text-[11px] font-semibold leading-4 text-gray-700 sm:text-xs">あなたの正答率</div>
              <div class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{{ selectedSubjectPersonalRate }}%</div>
            </div>
            <div
              v-if="isNationalAnalyticsReady"
              class="flex min-h-[94px] flex-col items-center justify-between rounded-xl border border-gray-200 bg-gray-100 px-3 py-3 text-center shadow-sm sm:px-4 sm:py-4">
              <div class="text-[11px] font-semibold leading-4 text-gray-600 sm:text-xs">全国平均</div>
              <div class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{{ selectedSubjectNationalRate }}%</div>
            </div>
            <div class="flex min-h-[94px] flex-col items-center justify-between rounded-xl border border-green-100 bg-green-50 px-3 py-3 text-center shadow-sm sm:px-4 sm:py-4">
              <div class="text-[11px] font-semibold leading-4 text-green-600 sm:text-xs">強い分野</div>
              <div class="space-y-1">
                <div class="break-words text-sm font-semibold leading-5 text-green-900 sm:text-base">
                  {{ strongestField?.name || '-' }}
                </div>
                <div class="text-2xl font-bold tracking-tight text-green-900 sm:text-3xl">
                  {{ strongestField?.personalRate ?? '-' }}%
                </div>
              </div>
            </div>
            <div class="flex min-h-[94px] flex-col items-center justify-between rounded-xl border border-red-100 bg-red-50 px-3 py-3 text-center shadow-sm sm:px-4 sm:py-4">
              <div class="text-[11px] font-semibold leading-4 text-red-600 sm:text-xs">弱い分野</div>
              <div class="space-y-1">
                <div class="break-words text-sm font-semibold leading-5 text-red-900 sm:text-base">
                  {{ weakestField?.name || '-' }}
                </div>
                <div class="text-2xl font-bold tracking-tight text-red-900 sm:text-3xl">
                  {{ weakestField?.personalRate ?? '-' }}%
                </div>
              </div>
            </div>
          </div>

          <div class="rounded-2xl border border-gray-100 bg-gray-50 p-3 sm:p-4">
            <div class="h-64 sm:h-80">
              <BarChart
                v-if="selectedFieldChartRows.length"
                :key="`${selectedSubjectId}-${selectedSubjectChartKey}`"
                :chart-data="selectedFieldChartData"
                css-classes="h-full w-full"
                :options="fieldBreakdownChartOptions"
                :styles="fieldBreakdownChartStyles" />
              <div
                v-else
                class="flex h-full items-center justify-center text-sm font-medium text-gray-500">
                表示できる分野別データがありません。
              </div>
            </div>
          </div>

          <div class="space-y-3 md:hidden">
            <div
              v-for="field in selectedFieldChartRows"
              :key="field.name"
              class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0 flex-1">
                  <div class="text-sm font-semibold leading-6 text-gray-900">
                    {{ field.name }}
                  </div>
                  <div class="mt-1 text-sm font-medium text-gray-700">
                    {{ formatFieldAnswerSummary(field) }}
                  </div>
                </div>
                <div class="shrink-0 text-right text-xs font-medium text-gray-500">
                  分野別
                </div>
              </div>

              <div class="mt-4 space-y-2">
                <div class="flex items-center justify-between rounded-lg bg-blue-50 px-3 py-2">
                  <span class="text-xs font-medium text-blue-600">あなた</span>
                  <span class="text-sm font-semibold text-blue-700">{{ field.personalRate }}%</span>
                </div>
                <div
                  v-if="isNationalAnalyticsReady"
                  class="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                  <span class="text-xs font-medium text-gray-500">全国</span>
                  <span class="text-sm font-semibold text-gray-700">{{ field.nationalRate }}%</span>
                </div>
                <div
                  v-if="isNationalAnalyticsReady"
                  class="flex items-center justify-between rounded-lg bg-white px-3 py-2 ring-1 ring-inset ring-gray-200">
                  <span class="text-xs font-medium text-gray-500">差分</span>
                  <span
                    class="text-sm font-semibold"
                    :class="getGapClass(field.personalRate - field.nationalRate)">
                    {{ formatGap(field.personalRate - field.nationalRate) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="hidden overflow-x-auto rounded-2xl border border-gray-200 md:block">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    分野
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    正答数
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    あなた
                  </th>
                  <th
                    v-if="isNationalAnalyticsReady"
                    class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    全国
                  </th>
                  <th
                    v-if="isNationalAnalyticsReady"
                    class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    差分
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100 bg-white">
                <tr
                  v-for="field in selectedFieldChartRows"
                  :key="field.name">
                  <td class="px-4 py-3 text-sm font-medium text-gray-900">
                    {{ field.name }}
                  </td>
                  <td class="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-700">
                    {{ formatFieldAnswerSummary(field) }}
                  </td>
                  <td class="px-4 py-3 text-right text-sm text-blue-700">
                    {{ field.personalRate }}%
                  </td>
                  <td
                    v-if="isNationalAnalyticsReady"
                    class="px-4 py-3 text-right text-sm text-gray-700">
                    {{ field.nationalRate }}%
                  </td>
                  <td
                    v-if="isNationalAnalyticsReady"
                    class="px-4 py-3 text-right text-sm font-medium"
                    :class="getGapClass(field.personalRate - field.nationalRate)">
                    {{ formatGap(field.personalRate - field.nationalRate) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <template #footer>
        <button
          type="button"
          class="inline-flex w-full justify-center rounded-md bg-gray-800 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-700 sm:w-auto"
          @click="closeSubjectModal">
          閉じる
        </button>
      </template>
    </DialogModal>
  </AppClientSchoolLayout>
</template>

<script setup>
import moment from 'moment'
import { Chart, registerables } from 'chart.js'
import { computed, nextTick, ref } from 'vue'
import { BarChart, RadarChart } from 'vue-chart-3'
import DialogModal from '@/Common/DialogModal'
import formatDate from '@/actions/format/date'
import convertToGrade from '@/actions/serviceYears/convert'
import AppClientSchoolLayout from '@/Layouts/AppClientSchoolLayout'
import { EXAMINATION_TYPES, getExaminationType } from '@/Constants/examinationTypes'

Chart.register(...registerables)

const props = defineProps({
  examination: {
    type: Object,
    default: () => ({}),
  },
  user: {
    type: Object,
    default: () => ({}),
  },
  progresses: {
    type: Array,
    default: () => [],
  },
  scores: {
    type: Array,
    default: () => [],
  },
  ranking: {
    type: Number,
    default: null,
  },
  usersCount: {
    type: Number,
    default: 0,
  },
  schoolRanking: {
    type: Number,
    default: null,
  },
  studentsCount: {
    type: Number,
    default: 0,
  },
  subjectAnalytics: {
    type: Object,
    default: () => ({}),
  },
  passFailResults: {
    type: Object,
    default: () => ({}),
  },
  fiscalYearStart: {
    type: String,
    required: true,
  },
})

const selectedExamination = computed(() => props.examination || null)
const currentExaminationId = computed(() => selectedExamination.value?.id || null)
const selectedSubjectId = ref(null)
const subjectChartRef = ref(null)
const selectedSubjectChartKey = ref(0)

const scoreRowDefinitions = [
  { label: '必須', type: EXAMINATION_TYPES.compulsory.index, max: 50 },
  { label: '一般', type: EXAMINATION_TYPES.general.index, max: 100 },
  { label: '実地', type: EXAMINATION_TYPES.practice.index, max: 50 },
]

const filterExamProgress = (type) => props.progresses.filter(progress => (progress.examination_type === type))

const isFinishSelectType = (type) => filterExamProgress(type).length > 0

const isFinishExam = () => props.progresses.some(progress => progress.examination_id === currentExaminationId.value)

const getScore = (type) => {
  const prog = filterExamProgress(type).pop()
  if (prog && prog.score !== null) return Number(prog.score)
  return 0
}

const getTotalScore = () => {
  let totalScore = 0

  Object.values(EXAMINATION_TYPES).forEach(examinationType => {
    totalScore += getScore(examinationType.index)
  })
  return totalScore
}

const getScorePercentage = (type) => {
  const prog = filterExamProgress(type).pop()
  if (!prog || !prog.score) return 0

  return prog.score * (100 / getExaminationType(type).questionsCount)
}

const getAverageScorePercentage = (type) => {
  const score = props.scores
    .filter(value => (value.examination_type === type))
    .pop()

  if (!score || !Object.keys(score).some(value => value === 'average_score')) return 0

  return score.average_score * (100 / getExaminationType(type).questionsCount)
}

const calcRank = (val, maxVal) => {
  const percentage = (val * 100) / maxVal

  if (percentage >= 80) return 'A'
  if (percentage >= 60) return 'B'
  if (percentage >= 40) return 'C'
  return 'D'
}

const getRank = (type) => {
  const prog = filterExamProgress(type).pop()
  if (!prog || !prog.score) return calcRank(0, 100)

  let intScore = parseInt(prog.score, 10)
  intScore *= (100 / (getExaminationType(type).questionsCount))
  return calcRank(intScore, 100)
}

const getGradeBadgeClass = (rank) => {
  const baseClass = 'inline-flex h-7 w-8 items-center justify-center justify-self-end rounded-md text-sm font-black text-white'

  if (rank === 'A') {
    return `${baseClass} bg-yellow-500`
  }

  if (rank === 'B') {
    return `${baseClass} bg-green-500`
  }

  if (rank === 'C') {
    return `${baseClass} bg-yellow-300 text-gray-900`
  }

  return `${baseClass} bg-blue-500`
}

const getPassFailChipClass = (result, variant = 'solid') => {
  const baseClass = 'inline-flex items-center justify-center rounded-full font-black tracking-normal'
  let sizeClass = 'min-h-[28px] min-w-[62px] px-3 text-xs'

  if (variant === 'solid') {
    sizeClass = 'min-h-[32px] min-w-[74px] px-4 text-lg'
  } else if (variant === 'outlineCompact') {
    sizeClass = 'min-h-[24px] min-w-[48px] px-2 text-[11px]'
  }

  if (result === '合格') {
    return `${baseClass} ${sizeClass} ${variant === 'solid'
      ? 'bg-green-600 text-white shadow-sm'
      : 'border border-green-500 bg-white text-green-700'}`
  }

  if (result === '不合格') {
    return `${baseClass} ${sizeClass} ${variant === 'solid'
      ? 'bg-red-600 text-white shadow-sm'
      : 'border border-red-500 bg-white text-red-600'}`
  }

  return `${baseClass} ${sizeClass} ${variant === 'solid'
    ? 'bg-gray-200 text-gray-700'
    : 'border border-gray-300 bg-white text-gray-500'}`
}

const getRanking = () => props.ranking || false
const getSchoolRanking = () => props.schoolRanking || false
const getSchoolUserNum = () => props.studentsCount || 0

const isOveredAggregationPeriod = (examination, type) => {
  const prog = props.progresses.filter(progress => (progress.examination_id === examination.id)
    && (progress.examination_type === type)).pop()

  if (!prog) {
    return false
  }

  const progStartAt = moment(prog.started_at)
  const resultAnnouncementDate = moment(examination.result_announcement_date)

  return progStartAt.isAfter(resultAnnouncementDate)
}

const fiscalYearStart = (date) => {
  const fyStart = moment(props.fiscalYearStart)
  const momentDate = moment(date)
  return momentDate.isBefore(fyStart)
}

const isFinishAllType = (id) => {
  const progresses = props.progresses.filter(progress => (progress.examination_id === id))

  if (progresses.length < 3) {
    return false
  }

  return progresses.every(element => element.finished_at !== null)
}

const currentPassFailResults = computed(() => {
  if (!currentExaminationId.value) {
    return []
  }

  return props.passFailResults[currentExaminationId.value] || []
})

const getCurrentPassFailResult = (label) => currentPassFailResults.value
  .find(result => result.label === label)

const overallPassFailResult = computed(() => {
  if (!currentExaminationId.value || !isFinishAllType(currentExaminationId.value)) {
    return '未判定'
  }

  return getCurrentPassFailResult('結果')?.result || '未判定'
})

const passScoreSummaries = computed(() => {
  if (!currentExaminationId.value) {
    return []
  }

  const isCompulsoryCompleted = isFinishSelectType(EXAMINATION_TYPES.compulsory.index)
  const isGeneralCompleted = isFinishSelectType(EXAMINATION_TYPES.general.index)
  const isPracticeCompleted = isFinishSelectType(EXAMINATION_TYPES.practice.index)

  return [
    {
      label: '必須',
      score: getScore(EXAMINATION_TYPES.compulsory.index),
      max: 50,
      completed: isCompulsoryCompleted,
      result: isCompulsoryCompleted ? (getCurrentPassFailResult('必須')?.result || '未判定') : '未実施',
    },
    {
      label: '一般＋実地',
      score: getScore(EXAMINATION_TYPES.general.index) + getScore(EXAMINATION_TYPES.practice.index),
      max: 150,
      completed: isGeneralCompleted && isPracticeCompleted,
      result: isGeneralCompleted && isPracticeCompleted
        ? (getCurrentPassFailResult('一般＋実地')?.result || '未判定')
        : '未実施',
    },
  ]
})

const resultScoreRows = computed(() => {
  if (!selectedExamination.value || !currentExaminationId.value) {
    return []
  }

  const rows = scoreRowDefinitions.map((definition) => {
    const completed = isFinishSelectType(definition.type)

    return {
      ...definition,
      completed,
      score: getScore(definition.type),
      rank: getRank(definition.type),
      isExcluded: completed
        && isOveredAggregationPeriod(selectedExamination.value, definition.type)
        && getRanking()
        && fiscalYearStart(selectedExamination.value.reception_start),
      isTotal: false,
    }
  })

  const totalScore = getTotalScore()

  rows.push({
    label: '合計',
    max: 200,
    completed: isFinishExam(),
    score: totalScore,
    rank: calcRank(totalScore, 200),
    isExcluded: false,
    isTotal: true,
  })

  return rows
})

const currentSubjectAnalytics = computed(() => {
  if (!currentExaminationId.value) {
    return null
  }

  return props.subjectAnalytics[currentExaminationId.value] || null
})

const isNationalAnalyticsReady = computed(() => currentSubjectAnalytics.value?.nationalAnalyticsReady === true)

const hasPersonalSubjectAnalytics = computed(() => currentSubjectAnalytics.value?.hasPersonalAnswerData === true)

const currentSubjectOptions = computed(() => currentSubjectAnalytics.value?.subjects || [])
const currentSubjectFieldBreakdowns = computed(() => currentSubjectAnalytics.value?.fieldBreakdowns || {})
const selectedFieldBreakdown = computed(() => currentSubjectFieldBreakdowns.value[selectedSubjectId.value] || [])
const selectedSubject = computed(() => currentSubjectOptions.value.find(subject => subject.id === selectedSubjectId.value))
const isSubjectModalOpen = computed(() => !!selectedSubjectId.value)

const hasSubjectAnalyticsDefinition = computed(() => currentSubjectOptions.value.length > 0)

const isCurrentExaminationFinished = computed(() => (
  currentExaminationId.value ? isFinishAllType(currentExaminationId.value) : false
))

const currentTotalScore = computed(() => getTotalScore())

const shouldShowSubjectAnalyticsMissingState = computed(() => (
  hasSubjectAnalyticsDefinition.value
    && isCurrentExaminationFinished.value
    && !hasPersonalSubjectAnalytics.value
    && currentTotalScore.value > 0
))

const shouldShowSubjectAnalytics = computed(() => (
  hasSubjectAnalyticsDefinition.value
    && isCurrentExaminationFinished.value
    && !shouldShowSubjectAnalyticsMissingState.value
))

const selectedSubjectLabel = computed(() => {
  if (!selectedSubject.value) {
    return ''
  }

  return selectedSubject.value.label
})

const getShortSubjectLabel = (label) => ({
  基礎動物学: '基礎',
  基礎動物看護学: '基礎看護',
  臨床動物看護学: '臨床看護',
  '愛護・適正飼養学': '愛護',
  実習: '実習',
}[label] || label)

const openSubjectModal = async (subjectId) => {
  selectedSubjectId.value = subjectId
  await nextTick()
  selectedSubjectChartKey.value += 1
}

const subjectBreakdownChartData = computed(() => {
  const datasets = [
    {
      label: 'あなたの正答率(%)',
      data: currentSubjectOptions.value.map(subject => subject.personalRate),
      backgroundColor: 'rgba(9, 169, 99, 0.14)',
      borderColor: '#09A963',
      pointBackgroundColor: '#09A963',
      pointBorderColor: '#FFFFFF',
      pointHoverBackgroundColor: '#09A963',
      pointHoverBorderColor: '#FFFFFF',
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2,
    },
  ]

  if (isNationalAnalyticsReady.value) {
    datasets.push({
      label: '全国平均(%)',
      data: currentSubjectOptions.value.map(subject => subject.nationalRate),
      backgroundColor: 'rgba(107, 114, 128, 0.14)',
      borderColor: '#9CA3AF',
      pointBackgroundColor: '#9CA3AF',
      pointBorderColor: '#FFFFFF',
      pointHoverBackgroundColor: '#9CA3AF',
      pointHoverBorderColor: '#FFFFFF',
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2,
    })
  }

  return {
    labels: currentSubjectOptions.value.map(subject => getShortSubjectLabel(subject.label)),
    datasets,
  }
})

const subjectBreakdownChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'nearest',
    intersect: false,
  },
  onClick: (_event, elements) => {
    if (!elements.length) {
      return
    }

    const subject = currentSubjectOptions.value[elements[0].index]

    if (subject) {
      openSubjectModal(subject.id)
    }
  },
  onHover: (_event, elements, chart) => {
    const { canvas } = chart
    canvas.style.cursor = elements.length ? 'pointer' : 'default'
  },
  plugins: {
    legend: {
      display: false,
    },
  },
  elements: {
    point: {
      hitRadius: 18,
    },
  },
  scales: {
    r: {
      beginAtZero: true,
      min: 0,
      max: 100,
      ticks: {
        stepSize: 20,
        callback: value => `${value}%`,
        backdropColor: 'transparent',
      },
      pointLabels: {
        font: {
          size: 10,
        },
      },
    },
  },
}))

const scoreBarChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
  },
  layout: {
    padding: {
      bottom: 0,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
      ticks: {
        stepSize: 10,
        callback: value => `${value}%`,
      },
    },
  },
}

const fieldBreakdownChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 700,
    easing: 'easeOutQuart',
  },
  animations: {
    x: {
      duration: 0,
    },
    base: {
      from: context => context.chart.scales.y.getBasePixel(),
    },
    y: {
      from: context => context.chart.scales.y.getBasePixel(),
    },
  },
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        boxHeight: 12,
        boxWidth: 32,
        padding: 10,
      },
    },
  },
  layout: {
    padding: {
      top: 12,
      right: 4,
      bottom: 0,
      left: 0,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        display: false,
      },
    },
    y: {
      beginAtZero: true,
      max: 100,
      ticks: {
        stepSize: 50,
        callback: value => `${value}%`,
      },
    },
  },
}

const fieldBreakdownChartStyles = {
  height: '100%',
  width: '100%',
}

const selectedSubjectPersonalRate = computed(() => {
  if (!selectedSubject.value) {
    return 0
  }

  return selectedSubject.value.personalRate
})

const selectedSubjectNationalRate = computed(() => {
  if (!selectedSubject.value || !isNationalAnalyticsReady.value) {
    return null
  }

  return selectedSubject.value.nationalRate
})

const selectedFieldChartRows = computed(() => selectedFieldBreakdown.value)

const strongestField = computed(() => {
  if (!selectedFieldChartRows.value.length) {
    return null
  }

  return [...selectedFieldChartRows.value].sort((left, right) => right.personalRate - left.personalRate)[0]
})

const weakestField = computed(() => {
  if (!selectedFieldChartRows.value.length) {
    return null
  }

  // 本人の正答率が最も低い分野を「弱い分野」として表示する。
  return [...selectedFieldChartRows.value].sort((left, right) => left.personalRate - right.personalRate)[0]
})

const selectedFieldChartData = computed(() => {
  const datasets = [
    {
      label: 'あなたの正答率',
      data: selectedFieldChartRows.value.map(field => field.personalRate),
      backgroundColor: '#2ECC71',
      borderRadius: 8,
      categoryPercentage: 0.72,
      barPercentage: 0.9,
      maxBarThickness: 18,
    },
  ]

  if (isNationalAnalyticsReady.value) {
    datasets.push({
      label: '全国正答率',
      data: selectedFieldChartRows.value.map(field => field.nationalRate),
      backgroundColor: '#9AE6B4',
      borderRadius: 8,
      categoryPercentage: 0.72,
      barPercentage: 0.9,
      maxBarThickness: 18,
    })
  }

  return {
    labels: selectedFieldChartRows.value.map(field => field.name),
    datasets,
  }
})

const getFieldQuestionCount = field => field.questionCount || 8

const getFieldCorrectCount = field => field.correctCount
  ?? Math.round((field.personalRate / 100) * getFieldQuestionCount(field))

const formatFieldAnswerSummary = (field) => {
  if (!field.questionCount) {
    return '-'
  }

  return `${getFieldCorrectCount(field)}問 / ${getFieldQuestionCount(field)}問`
}

const closeSubjectModal = () => {
  selectedSubjectId.value = null
}

const handleSubjectChartClick = (event) => {
  const chart = subjectChartRef.value?.chartInstance

  if (!chart) {
    return
  }

  const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: false }, true)

  if (!elements.length) {
    return
  }

  const subject = currentSubjectOptions.value[elements[0].index]

  if (subject) {
    openSubjectModal(subject.id)
  }
}

const formatGap = gap => `${gap > 0 ? '+' : ''}${gap}%`

const getGapClass = (gap) => {
  if (gap > 0) {
    return 'text-emerald-600'
  }

  if (gap < 0) {
    return 'text-rose-600'
  }

  return 'text-gray-500'
}

const getBarData = () => ({
  labels: ['必須', '一般', '実地'],
  datasets: [
    {
      type: 'bar',
      label: 'あなたの正答率(%)',
      data: [
        getScorePercentage(EXAMINATION_TYPES.compulsory.index),
        getScorePercentage(EXAMINATION_TYPES.general.index),
        getScorePercentage(EXAMINATION_TYPES.practice.index),
      ],
      fill: true,
      order: 0,
      backgroundColor: '#09A963',
      borderRadius: 6,
    },
    {
      type: 'bar',
      label: '全国平均(%)',
      data: [
        getAverageScorePercentage(EXAMINATION_TYPES.compulsory.index),
        getAverageScorePercentage(EXAMINATION_TYPES.general.index),
        getAverageScorePercentage(EXAMINATION_TYPES.practice.index),
      ],
      backgroundColor: '#D8DADD',
      borderRadius: 6,
      fill: true,
      order: 1,
    },
  ],
})

const isBeforeDate = (data) => {
  const now = moment()
  const momentDate = moment(data)
  return now.isBefore(momentDate)
}

const isOveredDate = (data) => {
  const now = moment()
  const momentDate = moment(data)
  return now.isAfter(momentDate)
}
</script>
